export const CHECKLIST_OVERVIEW = {
	title: 'Preventive Maintenance Checklist Library: Templates for Every System in Your Facility',
	intro: [
		"Unplanned downtime rarely starts with a dramatic failure. It starts small — a belt that's a little looser than it should be, a filter nobody swapped out, a valve that's been seizing up for months. A preventive maintenance checklist is what catches those small things before they become work orders you didn't plan for.",
		"This page is a working library, not a single template. Below you'll find a preventive maintenance checklist template for every major system in a commercial facility — HVAC, electrical, plumbing, fire safety, roofing, generators, industrial equipment, hotels, property portfolios, and fleet vehicles — each one downloadable and ready to load into your CMMS.",
	],
	whatIs: {
		title: 'What Is a Preventive Maintenance Checklist?',
		description:
			"A preventive maintenance checklist is a structured list of inspection, cleaning, lubrication, and testing tasks performed on a set schedule — before equipment fails, not after. Instead of waiting for a breakdown to tell you something's wrong, a preventative maintenance checklist gives technicians a repeatable routine for catching wear, corrosion, misalignment, and other early failure signs while they're still cheap to fix.",
		frameworkTitle: 'Every checklist on this page follows the same basic logic:',
		frameworkItems: [
			{ label: 'What to check', desc: 'the specific component, reading, or condition' },
			{ label: 'How often', desc: 'a starting frequency based on OEM guidance and typical usage' },
			{ label: 'What "pass" looks like', desc: 'a condition, range, or reading a technician can check against' },
			{ label: 'Who owns it', desc: 'the role responsible for completing and signing off on the task' },
		],
	},
	whyMatters: {
		title: 'Why Preventive Maintenance Checklists Matter',
		subtitle: 'Facilities that run on a documented PM checklist consistently see four things happen:',
		points: [
			{
				title: 'Less unplanned downtime',
				desc: 'Catching a worn belt or a loose terminal during a scheduled inspection is a five-minute fix. Catching it after it fails is a shutdown.',
			},
			{
				title: 'Lower repair costs',
				desc: 'Planned parts swaps and scheduled labor are cheaper than emergency callouts, rush freight, and overtime.',
			},
			{
				title: 'Longer asset life',
				desc: 'Lubrication, calibration, and cleaning tasks slow the wear that shortens equipment lifespan.',
			},
			{
				title: 'Cleaner audits',
				desc: 'A documented, dated preventive maintenance checklist is exactly what inspectors, insurers, and compliance auditors ask to see.',
			},
		],
	},
	howToUse: {
		title: 'How to Use These Checklists',
		subtitle: 'Each template below is a starting point, not a finished program. Before you roll one out:',
		steps: [
			{
				step: '1',
				title: 'Start with OEM guidance',
				desc: 'Manufacturer manuals set the baseline frequency for filters, belts, fluids, and inspections.',
			},
			{
				step: '2',
				title: 'Adjust for usage',
				desc: 'Equipment running three shifts needs more frequent checks than equipment running one.',
			},
			{
				step: '3',
				title: 'Adjust for the environment',
				desc: 'Heat, dust, moisture, and corrosive air all shorten the interval between checks.',
			},
			{
				step: '4',
				title: 'Load it into a CMMS',
				desc: "A preventive maintenance checklist template only creates value once it's tied to a scheduled work order, an owner, and a record of completion — a spreadsheet can't do any of that automatically.",
			},
		],
	},
};

export const CHECKLIST_DEPARTMENTS = [
	{
		id: 'hvac',
		navTitle: 'HVAC',
		heading: 'HVAC Preventive Maintenance Checklist',
		categoryName: 'Mechanical and Electrical',
		intro:
			'HVAC systems are typically the most expensive mechanical asset in a building, and the easiest to neglect until comfort complaints start rolling in. A solid hvac preventive maintenance checklist template covers both the heating and cooling sides of the system, since each has its own seasonal failure points.',
		outro:
			'This hvac checklist for preventive maintenance works for packaged rooftop units, split systems, and central plants alike — the specific tonnage and equipment count will change, but the task list stays largely the same.',
		groups: [
			{
				title: 'Heating system checklist',
				items: [
					'Clean or replace air filters',
					'Inspect burner assembly and heat exchanger',
					'Check for gas or carbon monoxide leaks',
					'Inspect electrical connections and wiring for wear',
					'Lubricate motor bearings and moving parts',
					'Inspect belts for cracking or fraying',
					'Test thermostats and heat pump operation',
					'Clear blockages from drain lines and flue system',
				],
			},
			{
				title: 'Cooling system checklist',
				items: [
					'Clean condenser and evaporator coils',
					'Check refrigerant charge and inspect for leaks',
					'Clear condensate drain lines and pans',
					'Replace worn belts and pulleys',
					'Test airflow and confirm even distribution',
					'Check electrical connections and control settings',
					'Clear debris from around the outdoor unit',
				],
			},
		],
	},
	{
		id: 'electrical',
		navTitle: 'Electrical',
		heading: 'Electrical Preventive Maintenance Checklist',
		categoryName: 'Mechanical and Electrical',
		intro:
			'Electrical failures are disproportionately dangerous compared to other facility systems, which is why an electrical preventive maintenance checklist leans heavily on thermal and connection checks rather than just visual inspection.',
		outro:
			'Because electrical work carries higher risk, this checklist should only be executed by qualified, trained personnel — and every reading should be logged for trend tracking, not just recorded as pass/fail.',
		groups: [
			{
				items: [
					'Inspect panels for heat marks, discoloration, or loose connections',
					'Tighten terminations to manufacturer torque spec',
					'Run thermal imaging scans on switchgear and motor control centers',
					'Test ground-fault and arc-fault protection',
					'Exercise breakers regularly to prevent seizing',
					'Inspect and clean contactors; replace if pitted or worn',
					'Check UPS units and battery backups, logging voltage readings',
					'Confirm labeling and arc-flash signage is current',
				],
			},
		],
	},
	{
		id: 'plumbing',
		navTitle: 'Plumbing',
		heading: 'Plumbing Preventive Maintenance Checklist',
		categoryName: 'Mechanical and Electrical',
		intro:
			"Plumbing failures are quiet until they aren't — a slow drip behind a wall can go unnoticed for months before it becomes a five-figure repair. This checklist should run on a semiannual cycle at minimum.",
		groups: [
			{
				items: [
					'Inspect visible piping and fixtures for drips, corrosion, or damaged insulation',
					'Record pressure, temperature, and flow at key gauges',
					'Cycle shut-off and isolation valves so they don\'t seize',
					'Test backflow preventers per local code, then tag and date',
					'Flush water heaters to clear sediment buildup',
					'Inspect pump seals, bearings, and couplers',
					'Check for slow drains and clear minor clogs before they become blockages',
				],
			},
		],
	},
	{
		id: 'fire-safety',
		navTitle: 'Fire & Life Safety',
		heading: 'Fire & Life Safety Preventive Maintenance Checklist',
		categoryName: 'Fire',
		intro:
			'Fire and life safety systems are the one category where "preventive" and "legally required" overlap almost completely — most of the tasks below are tied to a testing frequency set by local fire code, not just best practice.',
		groups: [
			{
				items: [
					'Confirm monthly fire extinguisher inspections and annual service tags',
					'Inspect sprinkler heads, risers, gauges, and control valves',
					'Run fire pump churn tests and record performance metrics',
					'Test pull stations, alarm panels, horns, strobes, and monitoring links',
					'Check emergency lighting and exit signage; replace failed bulbs or batteries',
					'Flush eyewash stations and safety showers',
					'Review emergency shutdown and evacuation procedures with staff',
				],
			},
		],
	},
	{
		id: 'facilities-property',
		navTitle: 'Facilities & Property',
		heading: 'Facilities & Property Management Preventive Maintenance Checklist',
		categoryName: 'Building',
		intro:
			"For teams managing an entire building — or a portfolio of them — a facilities preventive maintenance checklist needs to cover the building envelope and interior in addition to mechanical systems. This is the checklist a property management preventive maintenance checklist typically expands into once you're tracking multiple sites.",
		outro:
			"Multi-site teams should track completion rates by location, not just by task — a single underperforming building can drag down portfolio-wide compliance numbers if it isn't flagged early.",
		groups: [
			{
				title: 'Interior',
				items: [
					'Hallways clear of obstructions; anti-slip surfaces intact',
					'Walls and ceilings checked for moisture, chipping, or peeling',
					'Door and window locks and hinges lubricated and functional',
					'Elevator inspected and license current',
					'Outlets and switches tested for hazards or burnout',
				],
			},
			{
				title: 'Exterior',
				items: [
					'Walkways even and clear of hazards',
					'Signage accurate and legible',
					'Paint, siding, and sealant in good condition',
					'Parking lots and garages inspected for damage',
					'Gutters and drainage clear of debris',
					'Fencing intact and secure',
				],
			},
		],
	},
	{
		id: 'commercial-roof',
		navTitle: 'Roof',
		heading: 'Commercial Roof Preventive Maintenance Checklist',
		categoryName: 'Building',
		intro:
			"Roof failures are expensive precisely because they're invisible until water shows up somewhere it shouldn't. A commercial roof preventative maintenance checklist should run at minimum twice a year, plus after any major storm.",
		groups: [
			{
				items: [
					'Inspect membrane, flashing, and seams for cracking or separation',
					'Clear drains, scuppers, and gutters of debris',
					'Check for standing water or ponding after rainfall',
					'Inspect rooftop equipment curbs and penetrations for seal failure',
					'Look for signs of moisture intrusion on interior ceilings below the roof',
					'Document and photograph any damage for warranty and insurance records',
				],
			},
		],
	},
	{
		id: 'industrial-equipment',
		navTitle: 'Industrial',
		heading: 'Industrial & Manufacturing Equipment Preventive Maintenance Checklist',
		categoryName: 'Mechanical and Electrical',
		intro:
			'An industrial preventive maintenance checklist has to flex around dozens of different asset types, but the underlying task categories are consistent across almost all production equipment.',
		outro:
			'Because every machine on a production floor has its own OEM schedule, treat this list as the shared foundation and layer asset-specific tasks on top of it inside your CMMS.',
		groups: [
			{
				items: [
					'Inspect and lubricate moving parts — bearings, gears, chains, slides',
					'Check belt and chain alignment and tension',
					'Inspect guards, interlocks, and emergency stops',
					'Calibrate sensors, scales, and limit switches',
					'Inspect and replace worn tooling, seals, and gaskets',
					'Check hydraulic and pneumatic systems for leaks and pressure',
					'Confirm adequate clearance and housekeeping around equipment',
				],
			},
		],
	},
	{
		id: 'generator',
		navTitle: 'Generator',
		heading: 'Generator Preventive Maintenance Checklist',
		categoryName: 'Mechanical and Electrical',
		intro:
			"A backup generator only matters the one day everything else has failed — which is exactly why a preventive maintenance checklist for generators can't slip.",
		groups: [
			{
				items: [
					'Check oil, coolant, and fuel levels and quality',
					'Inspect belts, hoses, and connections for wear',
					'Test battery voltage and charging system',
					'Run the unit under load on a scheduled test cycle',
					'Inspect exhaust system for leaks or corrosion',
					'Confirm automatic transfer switch operates correctly',
					'Check block heater and starting aids in cold climates',
				],
			},
		],
	},
	{
		id: 'hotel',
		navTitle: 'Hotel',
		heading: 'Hotel Preventive Maintenance Checklist',
		categoryName: 'Building',
		intro:
			"Hospitality facilities carry the added pressure of guest experience — a broken air handler or a leaking faucet isn't just a maintenance issue, it's a review. A hotel preventive maintenance checklist typically layers guest-room checks on top of standard building systems.",
		groups: [
			{
				items: [
					'Test guest room HVAC units, thermostats, and airflow',
					'Inspect plumbing fixtures for leaks, water pressure, and drainage',
					'Check life safety systems in corridors and guest rooms',
					'Inspect pool and spa equipment, filtration, and chemical balance',
					'Test kitchen and laundry equipment on their own service cycle',
					'Inspect common areas, elevators, and exterior grounds',
					'Log guest-reported maintenance issues for pattern tracking across rooms',
				],
			},
		],
	},
	{
		id: 'fleet-vehicle',
		navTitle: 'Fleet & Vehicle',
		heading: 'Fleet & Vehicle Preventive Maintenance Checklist',
		categoryName: 'General',
		intro:
			'Facilities that operate their own vehicles and material handling equipment need a preventive maintenance checklist template that covers on-site machines as well as anything that leaves the property.',
		outro:
			'Keeping fleet checklists inside the same system as building maintenance means one dashboard shows every asset\'s status — not a separate spreadsheet nobody checks.',
		groups: [
			{
				title: 'Forklift preventive maintenance checklist',
				items: [
					'Check horn, lights, and brakes daily',
					'Inspect mast, forks, and chains for wear or damage',
					'Check tires and fluid levels',
					'Test seatbelt and operator presence controls',
				],
			},
			{
				title: 'Truck and semi truck preventive maintenance checklist',
				items: [
					'Inspect brakes, tires, and suspension components',
					'Check fluid levels — oil, coolant, transmission, brake fluid',
					'Test lights, signals, and mirrors',
					'Inspect belts, hoses, and battery condition',
					'Review DOT inspection and log compliance requirements',
				],
			},
			{
				title: 'Preventative car maintenance checklist (facility and pool vehicles)',
				items: [
					'Check oil, tire pressure, and tread depth',
					'Inspect brakes and wiper blades',
					'Test lights and signals',
					'Rotate tires and check alignment on schedule',
				],
			},
		],
	},
];

export const CHECKLIST_BUILD_STEPS = {
	title: 'How to Build Your Own Preventive Maintenance Checklist Template',
	subtitle:
		'If none of the templates above match your exact equipment mix, use this structure to build your own:',
	steps: [
		'List every asset in the category, grouped by criticality.',
		'Pull the OEM-recommended service interval for each one.',
		"Write each task as a single, unambiguous action — \"inspect belt tension\" rather than \"check belt.\"",
		'Attach a pass/fail condition or reading range to each task, not just a checkbox.',
		'Assign an owner and a frequency, then load the checklist into a scheduled work order.',
		'Revisit the checklist quarterly based on technician feedback and recent breakdowns.',
	],
};

export const CHECKLIST_FAQS = [
	{
		question:
			"What's the difference between a preventive maintenance checklist and a preventative maintenance checklist?",
		answer:
			'Nothing — "preventive" and "preventative" are used interchangeably in facilities management. Both describe scheduled, proactive maintenance performed before failure occurs.',
	},
	{
		question: 'How often should a preventive maintenance checklist be completed?',
		answer:
			'It depends on the asset. HVAC filters might need monthly attention, while a commercial roof inspection is typically twice a year. Start with OEM guidance, then adjust based on usage and environment.',
	},
	{
		question: 'Can I run preventive maintenance checklists from a spreadsheet?',
		answer:
			"You can start there, but spreadsheets can't send reminders, assign work orders, or track completion in real time. Most teams outgrow spreadsheets once they're managing more than a handful of assets.",
	},
	{
		question: 'What should be included in a preventive maintenance checklist template?',
		answer:
			'At minimum: the specific task, the frequency, a clear pass/fail condition, and an assigned owner. The best templates also capture parts used and technician notes for each completed task.',
	},
];

export const CHECKLIST_BOTTOM_CTA = {
	title: 'Turn These Checklists Into Scheduled Work Orders',
	description:
		'A checklist only prevents downtime if someone actually completes it on schedule. FacilityBot turns every template on this page into a recurring, mobile-ready work order — with automatic reminders, photo documentation, and a full maintenance history for every asset, so nothing falls through the cracks between inspections.',
	buttons: [
		{
			text: 'Book a Live Demo',
			url: '/request-a-demo',
			primary: true,
		},
		{
			text: "Explore FacilityBot's CMMS",
			url: '/features',
			primary: false,
		},
	],
};
